/**
 * Announcement bar in «inline» mode (sections/header-announcements.liquid).
 * Desktop shows every message in one row (pure CSS). On phones the row scrolls as a marquee:
 * this script turns the speed setting (px/s) into an animation duration and pauses while touched.
 */
class CourtzoneAnnounce extends HTMLElement {
  connectedCallback() {
    this.group = this.querySelector('.cz-announce__group:not(.cz-announce__group--clone)');
    if (!this.group) return;

    // Every message is visible in this mode, unlike the slider it was built for.
    this.group.querySelectorAll('[aria-hidden]').forEach((slide) => slide.setAttribute('aria-hidden', 'false'));

    // A second copy makes the marquee loop seamlessly (hidden on desktop and for screen readers).
    if (!this.querySelector('.cz-announce__group--clone')) {
      const clone = /** @type {HTMLElement} */ (this.group.cloneNode(true));
      clone.classList.add('cz-announce__group--clone');
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('a').forEach((link) => link.setAttribute('tabindex', '-1'));
      clone.querySelectorAll('[data-shopify-editor-block]').forEach((el) => el.removeAttribute('data-shopify-editor-block'));
      this.group.after(clone);
    }

    this.#updateDuration();
    this.resizeObserver = new ResizeObserver(() => this.#updateDuration());
    this.resizeObserver.observe(this.group);

    this.addEventListener('pointerdown', this.#pause);
    this.addEventListener('pointerup', this.#resume);
    this.addEventListener('pointercancel', this.#resume);
    this.addEventListener('pointerleave', this.#resume);
    this.addEventListener('mouseenter', this.#pause);
    this.addEventListener('mouseleave', this.#resume);
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    this.removeEventListener('pointerdown', this.#pause);
    this.removeEventListener('pointerup', this.#resume);
    this.removeEventListener('pointercancel', this.#resume);
    this.removeEventListener('pointerleave', this.#resume);
    this.removeEventListener('mouseenter', this.#pause);
    this.removeEventListener('mouseleave', this.#resume);
  }

  #updateDuration() {
    const speed = Number(this.dataset.speed) || 35;
    const width = this.group?.getBoundingClientRect().width ?? 0;
    if (width > 0) this.style.setProperty('--cz-announce-duration', `${(width / speed).toFixed(1)}s`);
  }

  #pause = () => this.classList.add('is-paused');

  #resume = () => this.classList.remove('is-paused');
}

if (!customElements.get('cz-announce')) customElements.define('cz-announce', CourtzoneAnnounce);
